import type { AirHockeyGame, Level } from "./types.js";
import { CANVAS_W, CANVAS_H, GOAL_SIZE, WALL_PADDING, PADDLE_RADIUS, PUCK_RADIUS, PADDLE_SPEED, MAX_SCORE, LEVEL_SPEEDS } from "./types.js";

const GOAL_LEFT = { top: (CANVAS_H - GOAL_SIZE) / 2, bottom: (CANVAS_H + GOAL_SIZE) / 2 };
const GOAL_RIGHT = { top: (CANVAS_H - GOAL_SIZE) / 2, bottom: (CANVAS_H + GOAL_SIZE) / 2 };

function isInGoal(x: number, y: number): "left" | "right" | null {
  const inGoalY = y >= GOAL_LEFT.top && y <= GOAL_LEFT.bottom;
  if (x <= 0 && inGoalY) return "left";
  if (x >= CANVAS_W && inGoalY) return "right";
  return null;
}

function pickSpeed(level: Level): number {
  const s = LEVEL_SPEEDS[level].puckSpeed;
  return s * (Math.random() < 0.5 ? 1 : -1);
}

function pickVy(level: Level): number {
  return (Math.random() - 0.5) * LEVEL_SPEEDS[level].puckSpeed * 0.35;
}

export function createGame(level: Level = "medium"): AirHockeyGame {
  return {
    p1: { x: 60, y: CANVAS_H / 2, radius: PADDLE_RADIUS },
    p2: { x: CANVAS_W - 60, y: CANVAS_H / 2, radius: PADDLE_RADIUS },
    puck: { x: CANVAS_W / 2, y: CANVAS_H / 2, vx: pickSpeed(level), vy: pickVy(level), radius: PUCK_RADIUS },
    score: [0, 0],
    winner: null,
    message: "First to 7 wins!",
    slowdown: 0,
    level,
  };
}

export function movePaddle(game: AirHockeyGame, player: 1 | 2, dir: -1 | 0 | 1): AirHockeyGame {
  if (game.winner) return game;
  const p = player === 1 ? { ...game.p1 } : { ...game.p2 };
  p.y = Math.max(WALL_PADDING + PADDLE_RADIUS, Math.min(CANVAS_H - WALL_PADDING - PADDLE_RADIUS, p.y + dir * PADDLE_SPEED * 0.016));
  if (player === 1) return { ...game, p1: p };
  return { ...game, p2: p };
}

export function tick(game: AirHockeyGame, dt: number): AirHockeyGame {
  if (game.winner) return game;
  const cfg = LEVEL_SPEEDS[game.level];
  const dtSec = Math.min(dt / 1000, 0.03);

  let puck = { ...game.puck, x: game.puck.x + game.puck.vx * dtSec, y: game.puck.y + game.puck.vy * dtSec };
  let { p1, p2 } = game;

  if (puck.y - puck.radius <= WALL_PADDING) { puck.y = WALL_PADDING + puck.radius; puck.vy = Math.abs(puck.vy); }
  if (puck.y + puck.radius >= CANVAS_H - WALL_PADDING) { puck.y = CANVAS_H - WALL_PADDING - puck.radius; puck.vy = -Math.abs(puck.vy); }

  const goalSide = isInGoal(puck.x, puck.y);
  if (goalSide) {
    const scorer = goalSide === "left" ? 2 : 1;
    const scores: [number, number] = [...game.score];
    scores[scorer - 1]++;
    const winner = scores[scorer - 1] >= MAX_SCORE ? scorer : null;
    return {
      p1: { ...p1, x: 60, y: CANVAS_H / 2 },
      p2: { ...p2, x: CANVAS_W - 60, y: CANVAS_H / 2 },
      puck: { x: CANVAS_W / 2, y: CANVAS_H / 2, vx: pickSpeed(game.level), vy: pickVy(game.level), radius: PUCK_RADIUS },
      score: scores,
      winner,
      message: winner ? `Player ${winner} wins!` : `Player ${scorer} scored! ${scores[0]}–${scores[1]}`,
      slowdown: 30,
      level: game.level,
    };
  }

  if (puck.x < WALL_PADDING + PUCK_RADIUS) {
    const inGoal = puck.y >= GOAL_LEFT.top && puck.y <= GOAL_LEFT.bottom;
    if (!inGoal) { puck.x = WALL_PADDING + PUCK_RADIUS; puck.vx = Math.abs(puck.vx); }
  }
  if (puck.x > CANVAS_W - WALL_PADDING - PUCK_RADIUS) {
    const inGoal = puck.y >= GOAL_RIGHT.top && puck.y <= GOAL_RIGHT.bottom;
    if (!inGoal) { puck.x = CANVAS_W - WALL_PADDING - PUCK_RADIUS; puck.vx = -Math.abs(puck.vx); }
  }

  for (const paddle of [p1, p2]) {
    const dx = puck.x - paddle.x;
    const dy = puck.y - paddle.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const minDist = puck.radius + paddle.radius;
    if (dist < minDist && dist > 0) {
      const nx = dx / dist;
      const ny = dy / dist;
      puck.x = paddle.x + nx * minDist;
      puck.y = paddle.y + ny * minDist;
      const dot = puck.vx * nx + puck.vy * ny;
      puck.vx = (puck.vx - 2 * dot * nx) * 1.05;
      puck.vy = (puck.vy - 2 * dot * ny) * 1.05;
      const speed = Math.sqrt(puck.vx * puck.vx + puck.vy * puck.vy);
      if (speed > cfg.maxSpeed) { puck.vx = (puck.vx / speed) * cfg.maxSpeed; puck.vy = (puck.vy / speed) * cfg.maxSpeed; }
    }
  }

  const slow = Math.max(0, game.slowdown - 1);
  const friction = slow > 0 ? 0.98 : 0.9995;
  puck.vx *= friction;
  puck.vy *= friction;

  if (Math.abs(puck.vx) < 5 && Math.abs(puck.vy) < 5 && slow === 0) {
    puck.vx = (Math.random() < 0.5 ? 1 : -1) * cfg.kickSpeed;
    puck.vy = (Math.random() - 0.5) * cfg.kickSpeed * 0.35;
  }

  return { ...game, p1, p2, puck, slowdown: slow };
}

export function getAIMove(game: AirHockeyGame): -1 | 0 | 1 {
  const diff = game.puck.y - game.p2.y;
  const ballComing = game.puck.vx < 0;
  if (!ballComing) {
    const centerDiff = CANVAS_H / 2 - game.p2.y;
    if (Math.abs(centerDiff) < 10) return 0;
    return Math.sign(centerDiff) as -1 | 1;
  }
  if (Math.abs(diff) < 8) return 0;
  return Math.sign(diff) as -1 | 1;
}
